import { useState, useEffect } from 'react'
import { ColumnConfig } from '@/components/ui/ColumnVisibilityControl'

interface UseColumnVisibilityOptions {
  storageKey: string
  defaultColumns: ColumnConfig[]
}

export function useColumnVisibility({ storageKey, defaultColumns }: UseColumnVisibilityOptions) {
  const [columns, setColumns] = useState<ColumnConfig[]>(defaultColumns)

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const savedVisibility = JSON.parse(saved) as Record<string, boolean>

        setColumns(prevColumns =>
          prevColumns.map(col => ({
            ...col,
            visible: savedVisibility[col.key] ?? col.visible
          }))
        )
      }
    } catch (error) {
      console.warn('Failed to load column visibility from localStorage:', error)
    }
  }, [storageKey])

  // Save to localStorage when columns change
  const updateColumnVisibility = (columnKey: string, visible: boolean) => {
    setColumns(prevColumns => {
      const updatedColumns = prevColumns.map(col =>
        col.key === columnKey ? { ...col, visible } : col
      )

      // Save visibility state to localStorage
      try {
        const visibilityState = updatedColumns.reduce((acc, col) => {
          acc[col.key] = col.visible
          return acc
        }, {} as Record<string, boolean>)

        localStorage.setItem(storageKey, JSON.stringify(visibilityState))
      } catch (error) {
        console.warn('Failed to save column visibility to localStorage:', error)
      }

      return updatedColumns
    })
  }

  // Reset to default column visibility
  const resetToDefaults = () => {
    setColumns(defaultColumns)

    // Save default visibility state to localStorage
    try {
      const visibilityState = defaultColumns.reduce((acc, col) => {
        acc[col.key] = col.visible
        return acc
      }, {} as Record<string, boolean>)

      localStorage.setItem(storageKey, JSON.stringify(visibilityState))
    } catch (error) {
      console.warn('Failed to save column visibility to localStorage:', error)
    }
  }

  // Apply a standard report template
  const applyTemplate = (templateColumnKeys: string[]) => {
    setColumns(prevColumns => {
      const updatedColumns = prevColumns.map(col => {
        // Required columns stay visible regardless of template
        if (col.required) {
          return col
        }

        // Set visibility based on whether the column is in the template
        return {
          ...col,
          visible: templateColumnKeys.includes(col.key)
        }
      })

      // Save visibility state to localStorage
      try {
        const visibilityState = updatedColumns.reduce((acc, col) => {
          acc[col.key] = col.visible
          return acc
        }, {} as Record<string, boolean>)

        localStorage.setItem(storageKey, JSON.stringify(visibilityState))
      } catch (error) {
        console.warn('Failed to save column visibility to localStorage:', error)
      }

      return updatedColumns
    })
  }

  // Get visible columns for the DataTable
  const visibleColumns = columns.filter(col => col.visible)

  return {
    columns,
    visibleColumns,
    updateColumnVisibility,
    resetToDefaults,
    applyTemplate
  }
}